import { sanitizePublicProfileLocation } from "../../../../core/utilities/publicProfileLocation";
import { Match } from "../../domain/entities/userUnjabbed";

export const SECOND_LOOK_EMPTY_MESSAGE =
	"There are no more crossed out members to review.";

const DISCOVERY_SESSION_STORAGE_KEY = "unjabbed.discovery.current-session";
const REINSTATED_SECOND_LOOK_STORAGE_KEY = "unjabbed.discovery.reinstated-second-look";
const FARTHER_HIGH_WATER_STORAGE_KEY = "unjabbed.discovery.farther-high-water";
const discoverySessionStorageKey = (userId: string): string =>
	`${DISCOVERY_SESSION_STORAGE_KEY}.${encodeURIComponent(userId)}`;
const reinstatedSecondLookStorageKey = (userId: string): string =>
	`${REINSTATED_SECOND_LOOK_STORAGE_KEY}.${encodeURIComponent(userId)}`;
const fartherHighWaterStorageKey = (userId: string): string =>
	`${FARTHER_HIGH_WATER_STORAGE_KEY}.${encodeURIComponent(userId)}`;

export interface StoredDiscoverySession {
	viewedProfiles: Match[];
	viewedProfileIds: string[];
	viewedFromContinueFartherIds?: string[];
	farthestViewedFartherDistance?: number;
	savedDiscoveryRadius?: number;
}

export const readPersistedFartherHighWater = (userId?: string): number | undefined => {
	if (typeof window === "undefined" || !userId) return undefined;
	try {
		const value = Number(
			window.localStorage.getItem(fartherHighWaterStorageKey(userId)),
		);
		return Number.isFinite(value) && value > 0 ? value : undefined;
	} catch {
		return undefined;
	}
};

export const writePersistedFartherHighWater = (
	userId: string | undefined,
	distance?: number,
): void => {
	if (
		typeof window === "undefined" ||
		!userId ||
		!Number.isFinite(distance) ||
		Number(distance) <= 0
	) return;
	try {
		window.localStorage.setItem(
			fartherHighWaterStorageKey(userId),
			Number(distance).toString(),
		);
	} catch {
		// Discovery must continue even if the browser cannot retain this boundary.
	}
};

export const readStoredDiscoverySession = (userId?: string): StoredDiscoverySession => {
	if (typeof window === "undefined" || !userId) {
		return {
			viewedProfiles: [],
			viewedProfileIds: [],
			viewedFromContinueFartherIds: [],
		};
	}
	const persistedFartherHighWater = readPersistedFartherHighWater(userId);
	try {
		const parsed = JSON.parse(
			window.sessionStorage.getItem(discoverySessionStorageKey(userId)) || "{}",
		);
		const safeProfiles = Array.isArray(parsed.viewedProfiles)
			? parsed.viewedProfiles.filter(
				(profile: unknown) => typeof profile === "object" && profile !== null &&
					typeof (profile as { id?: unknown }).id === "string",
			  ).map(sanitizePublicProfileLocation)
			: [];
		// Rewrite the legacy cache without dropping selection or progress fields.
		// Failed storage writes must never restore unsafe in-memory values.
		try {
			if (Object.prototype.hasOwnProperty.call(parsed, "viewedProfiles") && JSON.stringify(safeProfiles) !== JSON.stringify(parsed.viewedProfiles)) {
				window.sessionStorage.setItem(discoverySessionStorageKey(userId), JSON.stringify({ ...parsed, viewedProfiles: safeProfiles }));
			}
		} catch { /* Storage may be full or disabled; return the sanitized values. */ }
		return {
			viewedProfiles: safeProfiles,
			viewedProfileIds: Array.isArray(parsed.viewedProfileIds)
				? Array.from(new Set(parsed.viewedProfileIds.filter((id: unknown) => typeof id === "string" && id)))
				: [],
			viewedFromContinueFartherIds: Array.isArray(parsed.viewedFromContinueFartherIds)
				? Array.from(new Set(parsed.viewedFromContinueFartherIds.filter((id: unknown) => typeof id === "string" && id)))
				: [],
			farthestViewedFartherDistance: persistedFartherHighWater ??
				(Number.isFinite(parsed.farthestViewedFartherDistance)
					? Number(parsed.farthestViewedFartherDistance)
					: undefined),
			savedDiscoveryRadius: Number.isFinite(parsed.savedDiscoveryRadius)
				? Number(parsed.savedDiscoveryRadius)
				: undefined,
		};
	} catch {
		return {
			viewedProfiles: [],
			viewedProfileIds: [],
			viewedFromContinueFartherIds: [],
			farthestViewedFartherDistance: persistedFartherHighWater,
		};
	}
};

export const readPersistedReinstatedSecondLookUserIds = (userId?: string): string[] => {
	if (typeof window === "undefined" || !userId) return [];
	try {
		const parsed = JSON.parse(
			window.localStorage.getItem(reinstatedSecondLookStorageKey(userId)) || "[]",
		);
		return Array.isArray(parsed)
			? Array.from(new Set(parsed.filter((id: unknown) => typeof id === "string" && id)))
			: [];
	} catch {
		return [];
	}
};

export const writePersistedReinstatedSecondLookUserIds = (
	userId: string | undefined,
	userIds: string[],
): void => {
	if (typeof window === "undefined" || !userId) return;
	try {
		window.localStorage.setItem(
			reinstatedSecondLookStorageKey(userId),
			JSON.stringify(Array.from(new Set(userIds.filter(Boolean)))),
		);
	} catch {
		// Discovery must continue even if the browser cannot retain this exception.
	}
};

export const viewedExclusionsKeepingReinstatedProfilesEligible = (
	viewedProfileIds: string[],
	reinstatedSecondLookUserIds: string[],
): string[] => {
	const reinstatedIds = new Set(reinstatedSecondLookUserIds);
	return viewedProfileIds.filter((userId) => !reinstatedIds.has(userId));
};

export const writeStoredDiscoverySession = (
	userId: string | undefined,
	session: StoredDiscoverySession,
): void => {
	if (typeof window === "undefined" || !userId) return;
	try {
		window.sessionStorage.setItem(
			discoverySessionStorageKey(userId),
			JSON.stringify({ ...session, viewedProfiles: session.viewedProfiles.map(sanitizePublicProfileLocation) }),
		);
	} catch {
		// Discovery must continue even if the browser cannot retain this tab's session cache.
	}
};

export const filterUnseenDiscoveryProfiles = (
	profiles: Match[],
	excludedUserIds: string[] = [],
	minimumDistanceExclusive?: number,
	maximumDistanceInclusive?: number,
): Match[] => {
	const excluded = new Set(excludedUserIds);
	const hasMinimumDistance = Number.isFinite(minimumDistanceExclusive);
	const hasMaximumDistance = Number.isFinite(maximumDistanceInclusive);
	return profiles.filter((profile) => {
		if (excluded.has(profile.id)) return false;
		const distance = Number(profile.distance);
		if (hasMinimumDistance && (!Number.isFinite(distance) || distance <= minimumDistanceExclusive!)) {
			return false;
		}
		if (hasMaximumDistance && (!Number.isFinite(distance) || distance > maximumDistanceInclusive!)) {
			return false;
		}
		return true;
	});
};

export const restartDiscoveryRadius = (
	savedDistance: number,
	farthestViewedFartherDistance?: number,
): number => Math.max(
	savedDistance,
	Number.isFinite(farthestViewedFartherDistance)
		? Math.ceil(farthestViewedFartherDistance!)
		: savedDistance,
);

export const nextPreviousProfileIndex = (
	currentIndex: number,
	profileCount: number,
): number | null =>
	currentIndex < profileCount - 1 ? currentIndex + 1 : null;

export interface PreviousProfileReviewState {
	reviewingPrevious: boolean;
	previousProfileIndex: number;
	direction: number;
}

export const openPreviousProfileReview = (
	profileCount: number,
): PreviousProfileReviewState => ({
	reviewingPrevious: profileCount > 0,
	previousProfileIndex: Math.max(profileCount - 1, 0),
	direction: -1,
});

export const swipeLeftInPreviousProfileReview = (
	state: PreviousProfileReviewState,
	profileCount: number,
): PreviousProfileReviewState => {
	const nextIndex = nextPreviousProfileIndex(
		state.previousProfileIndex,
		profileCount,
	);
	return nextIndex === null
		? { ...state, reviewingPrevious: false, direction: 1 }
		: { ...state, previousProfileIndex: nextIndex, direction: 1 };
};

export const rememberViewedProfile = (
	profiles: Match[],
	currentProfile: Match,
): Match[] => [
	...profiles.filter((profile) => profile.id !== currentProfile.id).map(sanitizePublicProfileLocation),
	sanitizePublicProfileLocation(currentProfile),
];
